import classNames from 'classnames';
import { motion } from 'framer-motion';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Brand from '../Brand/Brand';
import Navigation, { NavigationLine, NavigationTitle } from '../Navigation/Navigation';
import User from '../User/User';

import ThemeContext from '../../contexts/themeContext';

import { useMediaQuery } from 'react-responsive';
import Select from '../../components/bootstrap/forms/Select';
import Option from '../../components/bootstrap/Option';
import Tooltips from '../../components/bootstrap/Tooltips';
import AuthContext from '../../contexts/authContext';
import CompanieContext from '../../contexts/companieContext';
import useAsideTouch from '../../hooks/useAsideTouch';
import useDarkMode from '../../hooks/useDarkMode';
import { modules } from '../../sideBarMenu';
import { getAPI } from '../../apis/backend/apiNode';
// import { searchCompanies } from '../../odooApi/Company';

const MODULES_STORAGE_KEY = 'app_modules';

const Aside = () => {
	const { asideStatus, setAsideStatus } = useContext(ThemeContext);
	const [modulesMenu, setModulesMenu] = useState([]);
	const { asideStyle, touchStatus, hasTouchButton, asideWidthWithSpace, x } = useAsideTouch();

	const isModernDesign = process.env.REACT_APP_MODERN_DESGIN === 'true';
	const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });
	const isDesktop = !isMobile;

	const constraintsRef = useRef(null);

	const { userData } = useContext(AuthContext);

	const onToggle = React.useCallback(() => {
		setAsideStatus((prev) => !prev);
	}, [setAsideStatus]);

	const loadModules = async () => {
		const cachedModules = localStorage.getItem(MODULES_STORAGE_KEY);
		if (!!cachedModules) {
			const parsedModules = JSON.parse(cachedModules);
			if (!!parsedModules?.length) {
				setModulesMenu(parsedModules);
			} else {
				await fetchData();
			}
		} else {
			await fetchData();
		}
	};

	const fetchData = async () => {
		try {
			const res = await getAPI('module');
			const modulesArray = Array.isArray(res) ? res : [];
			setModulesMenu(modulesArray);

			// Save to localStorage with timestamp
			localStorage.setItem(MODULES_STORAGE_KEY, JSON.stringify(modulesArray));
		} catch (error) {
			// If fetch fails, try to use cached data as fallback
			const cachedModules = localStorage.getItem(MODULES_STORAGE_KEY);
			if (cachedModules) {
				try {
					const parsedModules = JSON.parse(cachedModules);
					setModulesMenu(Array.isArray(parsedModules) ? parsedModules : []);
				} catch (parseError) {
					setModulesMenu([]);
				}
			}
		}
	};

	useEffect(() => {
		loadModules();
	}, []);

	return (
		<>
			<motion.aside
				style={asideStyle}
				className={classNames(
					'aside',
					{ open: isDesktop || (isMobile && asideStatus) },
					{
						'aside-touch-bar': hasTouchButton && isModernDesign,
						'aside-touch-bar-close': !touchStatus && hasTouchButton && isModernDesign,
						'aside-touch-bar-open': touchStatus && hasTouchButton && isModernDesign,
					},
				)}>
				<div className='aside-head'>
					<Brand hideToggle={isDesktop} onToggle={onToggle} />
				</div>
				<div className='aside-body'>
					{!!modulesMenu?.length &&
						modulesMenu.map((module) => {
							if (module?.subModules?.length) {
								return (
									<Navigation
										key={module._id}
										menu={module.subModules?.map((item) => ({
											id: item?.id,
											icon: item?.icon,
											text: item?.name,
											path: item?.route?.path,
										}))}
										id={module.name}
									/>
								);

							}
						})}

					{Object.entries(modules).map(([moduleId, moduleItems]) => {
						const hasModulePermission = moduleItems.permissions?.includes(
							userData?.role?.toLowerCase()
						);

						if (!hasModulePermission) return null;

						const subModules = Object.values(moduleItems).filter(
							(v) => v?.path
						);

						return (
							<Navigation
								menu={subModules.map((sub) => ({
									id: sub.id,
									icon: sub.icon,
									text: sub.text,
									path: sub.path,
								}))}
								id={moduleId}
							/>
						);
					})}
				</div>
				<div className='aside-foot'>
					<User />
				</div>
			</motion.aside>
			{asideStatus && hasTouchButton && isModernDesign && (
				<>
					<motion.div className='aside-drag-area' ref={constraintsRef} />
					<Tooltips title='Toggle Aside' flip={['top', 'right']}>
						<motion.div
							className='aside-touch'
							drag='x'
							whileDrag={{ scale: 1.2 }}
							whileHover={{ scale: 1.1 }}
							dragConstraints={constraintsRef}
							// onDrag={(event, info) => console.log(info.point.x, info.point.y)}
							dragElastic={0.1}
							style={{ x, zIndex: 1039 }}
							onClick={() => x.set(x.get() === 0 ? asideWidthWithSpace : 0)}
						/>
					</Tooltips>
				</>
			)}
		</>
	);
};

export default Aside;
